/**
 * TanStack Query hooks — Files (uploads PDF & attachments).
 *
 * Routes API consommées (voir `apps/api/src/routes/files.ts`) :
 *   POST   /v1/files/uploads/initiate           initiate (signed URL)
 *   POST   /v1/files/uploads/:id/complete       complete (HEAD bucket → ready)
 *   GET    /v1/files?cursor&limit&project_id…   listing paginé (uploaded_at desc)
 *   GET    /v1/files/:id                        metadata
 *   GET    /v1/files/:id/download               signed download URL (5 min)
 *   DELETE /v1/files/:id                        soft-delete + remove bucket
 *
 * Caveat dette future : `FileRow` est aujourd'hui défini ici plutôt que dans
 * `@kollectivia/shared/schemas/db-rows.ts`. À promouvoir au moment où l'API
 * MCP exposera ces lignes (Chunk 11.B). Tracé comme D24 dans PROJECT_STATUS.
 */

import type { Schemas } from "@kollectivia/shared";
import type { DbPage } from "@kollectivia/shared";
import {
  type CompleteFileUploadInput,
  type FileFilter,
  type FileStatus,
  type InitiateFileUploadInput,
} from "@kollectivia/shared/schemas/files.js";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { api } from "../api";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * Lecture API — colonnes exposées par `FILE_PUBLIC_COLS` côté backend.
 * Volontairement défensif sur les nullables : tout ce qui peut être null en DB
 * (project_id, submission_id, company_id, sha256, page_count, uploaded_by) est
 * typé `| null`.
 */
export type FileRow = {
  document_context?: Schemas.FileDocumentContext | null;
  id: string;
  tenant_id: string;
  project_id: string | null;
  submission_id: string | null;
  company_id: string | null;
  storage_bucket: string;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  sha256: string | null;
  // Read rows also include server-generated artifacts; upload kinds remain restricted.
  kind: string;
  page_count: number | null;
  status: FileStatus;
  uploaded_by: string | null;
  uploaded_at: string; // ISO timestamp
};

// PostgreSQL bigint is serialized as a decimal string by metadata routes.
// Normalize at the read boundary; never weaken attachment snapshot comparisons.
type FileWireRow = Omit<FileRow, "size_bytes"> & { size_bytes: number | string };
function normalizeFile(row: FileWireRow): FileRow {
  const wireSize = row.size_bytes;
  const size =
    typeof wireSize === "number"
      ? wireSize
      : typeof wireSize === "string" && /^(0|[1-9][0-9]*)$/.test(wireSize)
        ? Number(wireSize)
        : NaN;
  if (!Number.isSafeInteger(size) || size < 0) throw new Error("Invalid file metadata size");
  return { ...row, size_bytes: size };
}

export type FileListResult = DbPage<FileRow>;

export type FileListParams = {
  cursor?: string | null;
  limit?: number;
} & Partial<FileFilter>;

/**
 * Réponse de `POST /v1/files/uploads/initiate` — paire `{ file, upload }`.
 * Le client doit faire un `PUT` vers `upload.url` avec le bon Content-Type.
 */
export type InitiateUploadResult = {
  file: FileRow;
  upload: {
    url: string;
    method: "PUT";
    headers: Record<string, string>;
    expires_at: string; // ISO
  };
};

export type DownloadUrlResult = {
  url: string;
  expires_at: string;
  file_name: string;
};

// -----------------------------------------------------------------------------
// Query keys (centralisés pour faciliter les invalidations)
// -----------------------------------------------------------------------------

export const filesKeys = {
  all: ["files"] as const,
  list: (params: FileListParams) => ["files", "list", params] as const satisfies QueryKey,
  detail: (id: string) => ["files", "detail", id] as const satisfies QueryKey,
  byProject: (projectId: string) =>
    ["files", "list", { project_id: projectId }] as const satisfies QueryKey,
  bySubmission: (submissionId: string) =>
    ["files", "list", { submission_id: submissionId }] as const satisfies QueryKey,
};

// -----------------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------------

function buildListPath(params: FileListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.project_id) sp.set("project_id", params.project_id);
  if (params.submission_id) sp.set("submission_id", params.submission_id);
  if (params.company_id) sp.set("company_id", params.company_id);
  if (params.kind) sp.set("kind", params.kind);
  if (params.status) sp.set("status", params.status);
  const qs = sp.toString();
  return qs ? `/v1/files?${qs}` : "/v1/files";
}

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

export function useFiles(params: FileListParams) {
  return useQuery({
    queryKey: filesKeys.list(params),
    queryFn: async (): Promise<FileListResult> => {
      const page = await api.get<DbPage<FileWireRow>>(buildListPath(params));
      return { ...page, items: page.items.map(normalizeFile) };
    },
    placeholderData: (prev) => prev,
  });
}

export function useFile(id: string | undefined) {
  return useQuery({
    queryKey: id ? filesKeys.detail(id) : filesKeys.detail("__none__"),
    queryFn: async () => normalizeFile(await api.get<FileWireRow>(`/v1/files/${id}`)),
    enabled: !!id,
  });
}

// -----------------------------------------------------------------------------
// Writes — initiate / complete / delete
// -----------------------------------------------------------------------------

/**
 * Étape 1 du flow d'upload — réserve une row `app.files` en `pending`
 * et obtient une signed URL.
 */
export function useInitiateFileUpload() {
  return useMutation({
    mutationFn: (input: InitiateFileUploadInput) =>
      api.post<InitiateUploadResult>(
        "/v1/files/uploads/initiate",
        input as unknown as Record<string, unknown>,
      ),
  });
}

/**
 * Étape 3 du flow d'upload — confirme que le binaire est arrivé dans
 * le bucket. Idempotent : si déjà `ready`, retourne tel quel.
 */
export function useCompleteFileUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, input }: { fileId: string; input: CompleteFileUploadInput }) =>
      api.post<FileRow>(
        `/v1/files/uploads/${fileId}/complete`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (file) => {
      // Invalide à la fois la fiche et toutes les listes (project, submission…).
      void qc.invalidateQueries({ queryKey: filesKeys.detail(file.id) });
      void qc.invalidateQueries({ queryKey: filesKeys.all });
    },
  });
}

export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/v1/files/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: filesKeys.all });
    },
  });
}

/**
 * Demande une signed URL de download (TTL 5 min côté API).
 * Implémenté en `mutateAsync` plutôt que `useQuery` parce qu'on ne veut PAS
 * que TanStack la cache : chaque demande doit produire une URL fraîche.
 */
export function useFileDownloadUrl() {
  return useMutation({
    mutationFn: (id: string) => api.get<DownloadUrlResult>(`/v1/files/${id}/download`),
  });
}
